/**
 * Property Form - Details Section (bedrooms, bathrooms, etc.)
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/utils/api";

const PropertyDetails = ({ 
  bedrooms, setBedrooms, 
  bathrooms, setBathrooms, 
  parkingSpaces, setParkingSpaces, 
  description, setDescription,
  // Additional props needed for AI generation
  name, propertyType, transactionType, priceUsd, areaM2, zone, department, features
}) => {
  const [generating, setGenerating] = useState(false);

  const handleGenerateDescription = async () => {
    if (!name || !propertyType || !transactionType || !priceUsd || !areaM2 || !zone) {
      toast.error("Por favor completa la información básica primero (nombre, tipo, precio, área, zona)");
      return;
    }

    setGenerating(true);
    try {
      const response = await apiClient.post('/ai/generate-description', {
        name,
        property_type: propertyType,
        transaction_type: transactionType,
        price_usd: parseFloat(priceUsd),
        area_m2: parseFloat(areaM2),
        zone,
        department: department || "Guatemala",
        bedrooms: parseInt(bedrooms) || 0,
        bathrooms: parseInt(bathrooms) || 0,
        parking_spaces: parseInt(parkingSpaces) || 0,
        features: features || []
      });

      setDescription(response.data.description);
      toast.success("¡Descripción generada con IA!");
    } catch (error) {
      toast.error("Error al generar la descripción");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="bg-[#121212] border border-white/5 p-6 space-y-6">
      <h2 className="text-lg text-white font-medium">Detalles</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Habitaciones</label>
          <Input 
            type="number" 
            value={bedrooms} 
            onChange={(e) => setBedrooms(e.target.value)} 
            placeholder="3" 
            className="h-12 bg-black/40 border-white/10 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Baños</label>
          <Input 
            type="number" 
            value={bathrooms} 
            onChange={(e) => setBathrooms(e.target.value)} 
            placeholder="2" 
            className="h-12 bg-black/40 border-white/10 text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Parqueos</label>
          <Input 
            type="number" 
            value={parkingSpaces} 
            onChange={(e) => setParkingSpaces(e.target.value)} 
            placeholder="2" 
            className="h-12 bg-black/40 border-white/10 text-white"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm text-[#A3A3A3]">Descripción</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerateDescription}
            disabled={generating}
            className="text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]"
            data-testid="generate-description-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generar con IA
          </Button>
        </div>
        <Textarea 
          value={description} 
          onChange={(e) => setDescription(e.target.value)} 
          placeholder="Describe las características principales de la propiedad..." 
          className="min-h-[150px] bg-black/40 border-white/10 text-white resize-none"
        />
        <p className="text-[#666] text-xs mt-1">Los saltos de línea se preservarán en la visualización</p>
      </div>
    </section>
  );
};

export default PropertyDetails;
